import React, { useState, useEffect } from 'react';
import { Brain, Clock, Hash, DollarSign, CheckCircle, XCircle } from 'lucide-react';
import deepseekDrillApi from '../services/deepseekDrillApi';

const AIDrillModal = ({ 
  isOpen, 
  onClose, 
  originalQuestion, 
  questionType, 
  grade, 
  onDrillGenerated 
}) => {
  const [progress, setProgress] = useState(0);
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [tokensUsed, setTokensUsed] = useState(0);
  const [estimatedTokens, setEstimatedTokens] = useState(2300);
  const [currentStep, setCurrentStep] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);

  const steps = [
    "分析原始題目結構",
    "生成相似題目模式", 
    "創建 5 道練習題",
    "生成詳細解答",
    "驗證題目完整性"
  ];

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      // Reset all states when modal opens
      setProgress(0);
      setTimeElapsed(0);
      setTokensUsed(0);
      setCurrentStep(0);
      setIsGenerating(false);
      setError(null);
    }
  }, [isOpen]);

  // Start generation when modal opens and not already generating
  useEffect(() => {
    if (isOpen && !isGenerating) {
      startGeneration();
    }
  }, [isOpen, isGenerating]);

  useEffect(() => {
    let interval;
    if (isGenerating) {
      interval = setInterval(() => {
        setTimeElapsed(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isGenerating]);

  const startGeneration = async () => {
    setIsGenerating(true);
    setProgress(0);
    setTimeElapsed(0);
    setCurrentStep(0);
    setError(null);

    try {
      // Simulate progress steps
      for (let i = 0; i < steps.length; i++) {
        setCurrentStep(i);
        setProgress((i + 1) * 20);
        await new Promise(resolve => setTimeout(resolve, 800 + Math.random() * 1200));
      }

      // Generate drill questions
      const result = await deepseekDrillApi.generateDrillQuestions(
        originalQuestion, 
        questionType, 
        'primary', 
        grade
      );

      setTokensUsed(result.metadata?.totalTokens || 0);
      setProgress(100);
      
      // Close modal and pass results
      setTimeout(() => {
        onDrillGenerated(result);
        onClose();
      }, 1000);

    } catch (err) {
      setError(err.message);
      setIsGenerating(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatCost = (cost) => {
    return `¥${cost.toFixed(6)}`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <Brain className="w-8 h-8 text-white" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">
            AI 練習題生成中
          </h3>
          <p className="text-gray-600 text-sm">
            正在分析題目並生成相似練習題
          </p>
        </div>

        {/* Progress Bar */}
        <div className="mb-6">
          <div className="flex justify-between text-xs text-gray-600 mb-2">
            <span>開始生成</span>
            <span>完成</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-gradient-to-r from-blue-500 to-purple-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
          <div className="text-center text-sm font-medium text-gray-700 mt-1">
            {progress}%
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="flex items-center space-x-2">
            <Clock className="w-4 h-4 text-blue-600" />
            <div>
              <div className="text-xs text-gray-600">已用時間</div>
              <div className="text-sm font-medium">{formatTime(timeElapsed)}</div>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Hash className="w-4 h-4 text-green-600" />
            <div>
              <div className="text-xs text-gray-600">已用 Token</div>
              <div className="text-sm font-medium">
                {tokensUsed || 0} / {estimatedTokens} 預估
              </div>
            </div>
          </div>
        </div>

        {/* Generation Steps */}
        <div className="space-y-3 mb-6">
          {steps.map((step, index) => (
            <div key={index} className="flex items-center space-x-3">
              <div className={`w-3 h-3 rounded-full ${
                index < currentStep 
                  ? 'bg-green-500' 
                  : index === currentStep 
                    ? 'bg-orange-500' 
                    : 'bg-gray-300'
              }`}></div>
              <span className={`text-sm ${
                index <= currentStep ? 'text-gray-900' : 'text-gray-500'
              }`}>
                {step}
              </span>
            </div>
          ))}
        </div>

        {/* Estimated Time */}
        <div className="text-center text-sm text-gray-600 mb-4">
          預計完成時間: 10-15 秒
        </div>
        <div className="text-center text-xs text-gray-500">
          請耐心等待, AI 正在為您生成高質量的練習題
        </div>

        {/* Error Display */}
        {error && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center space-x-2">
              <XCircle className="w-4 h-4 text-red-600" />
              <span className="text-sm text-red-700">生成失敗: {error}</span>
            </div>
          </div>
        )}

        {/* Close Button (only show when not generating) */}
        {!isGenerating && (
          <div className="mt-4 flex justify-center">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
            >
              關閉
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AIDrillModal; 